import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Building2, MapPin, ArrowLeft, Share2, Download, TrendingUp, TrendingDown,
  Bed, Bath, Square, Clock, Home, BarChart3, Map, FileText, Sparkles,
  Loader2, ExternalLink, Copy, Check
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ReportPage = ({ auth }) => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportContentRef = useRef(null);

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}`, {
        withCredentials: true
      });
      setReport(response.data);
    } catch (error) {
      toast.error("Failed to load report");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const response = await axios.post(`${API}/reports/${reportId}/share`, {}, {
        withCredentials: true
      });
      const shareUrl = `${window.location.origin}/shared/${response.data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied to clipboard!");
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error("Failed to create share link");
    } finally {
      setShareLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/export/csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rentlytics_report_${reportId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully!");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const handleExportPDF = async () => {
    if (!report) return;
    
    setPdfLoading(true);
    toast.info("Generating PDF report...");
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;
      
      // Helper function to add text with word wrap
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach((line, i) => {
          if (y + (i * lineHeight) > pageHeight - margin) {
            pdf.addPage();
            y = margin;
          }
          pdf.text(line, x, y + (i * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };
      
      // Header
      pdf.setFillColor(15, 23, 42); // navy-900
      pdf.rect(0, 0, pageWidth, 35, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rentlytix', margin, 18);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Rental Market Analysis Report', margin, 28);
      
      yPos = 45;
      
      // Property Address
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Property Details', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      pdf.text(report.address, margin, yPos);
      yPos += 6;
      pdf.text(`${report.city}, ${report.state} ${report.zip_code}`, margin, yPos);
      yPos += 10;
      
      // Property specs
      const specs = [
        `Type: ${report.property_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
        `Bedrooms: ${report.bedrooms}`,
        `Bathrooms: ${report.bathrooms}`,
        report.sqft ? `Sqft: ${report.sqft.toLocaleString()}` : null
      ].filter(Boolean);
      
      pdf.setFontSize(10);
      specs.forEach(spec => {
        pdf.text(spec, margin, yPos);
        yPos += 5;
      });
      yPos += 10;
      
      // Rent Estimate Box
      pdf.setFillColor(240, 253, 250); // teal-50
      pdf.setDrawColor(45, 212, 191); // teal-400
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 45, 3, 3, 'FD');
      
      yPos += 8;
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Estimated Monthly Rent', margin + 5, yPos);
      yPos += 12;
      
      pdf.setFontSize(28);
      pdf.setTextColor(13, 148, 136); // teal-600
      pdf.text(`$${report.rent_estimate?.estimated_rent?.toLocaleString() || 'N/A'}`, margin + 5, yPos);
      yPos += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(71, 85, 105);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Range: $${report.rent_estimate?.rent_low?.toLocaleString()} - $${report.rent_estimate?.rent_high?.toLocaleString()}`, margin + 5, yPos);
      yPos += 5;
      pdf.text(`Market Average: $${report.rent_estimate?.rent_average?.toLocaleString()}  |  Median: $${report.rent_estimate?.rent_median?.toLocaleString()}`, margin + 5, yPos);
      
      yPos += 20;
      
      // Market Statistics
      pdf.setTextColor(15, 23, 42);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Market Statistics', margin, yPos);
      yPos += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(71, 85, 105);
      
      const stats = [
        ['25th Percentile', `$${report.rent_estimate?.percentile_25?.toLocaleString()}`],
        ['75th Percentile', `$${report.rent_estimate?.percentile_75?.toLocaleString()}`],
        ['Comparable Properties', `${report.rent_estimate?.comps_count || report.comparables?.length || 0}`],
      ];
      
      stats.forEach(([label, value]) => {
        pdf.text(`${label}: ${value}`, margin, yPos);
        yPos += 5;
      });
      
      yPos += 10;
      
      // AI Insights
      if (report.ai_insights) {
        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('AI Market Insights', margin, yPos);
        yPos += 8;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        yPos = addWrappedText(report.ai_insights, margin, yPos, pageWidth - (margin * 2), 5);
        yPos += 10;
      }
      
      // Comparables Table
      if (report.comparables?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          yPos = margin;
        }
        
        pdf.setTextColor(15, 23, 42);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Comparable Properties', margin, yPos);
        yPos += 10;
        
        // Table header
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin, yPos - 4, pageWidth - (margin * 2), 8, 'F');
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(71, 85, 105);
        
        const colWidths = [70, 25, 20, 25, 25];
        const headers = ['Address', 'Rent', 'Beds', 'Baths', 'Distance'];
        let xPos = margin;
        headers.forEach((header, i) => {
          pdf.text(header, xPos + 2, yPos);
          xPos += colWidths[i];
        });
        yPos += 8;
        
        // Table rows
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(51, 65, 85);
        
        report.comparables.slice(0, 10).forEach((comp, index) => {
          if (yPos > pageHeight - 15) {
            pdf.addPage();
            yPos = margin;
          }
          
          if (index % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin, yPos - 4, pageWidth - (margin * 2), 7, 'F');
          }
          
          xPos = margin;
          const row = [
            comp.address?.substring(0, 35) + (comp.address?.length > 35 ? '...' : ''),
            `$${comp.rent?.toLocaleString()}`,
            comp.bedrooms?.toString(),
            comp.bathrooms?.toString(),
            `${comp.distance_miles?.toFixed(1)} mi`
          ];
          
          row.forEach((cell, i) => {
            pdf.text(cell || '-', xPos + 2, yPos);
            xPos += colWidths[i];
          });
          yPos += 7;
        });
      }
      
      // Footer
      const footerY = pageHeight - 10;
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`Generated by Rentlytix on ${new Date().toLocaleDateString()}`, margin, footerY);
      pdf.text(`Report ID: ${reportId}`, pageWidth - margin - 40, footerY);
      
      // Save PDF
      pdf.save(`rentalytix_report_${report.address?.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
      toast.success("PDF exported successfully!");
      
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!report) return null;

  const rentEstimate = report.rent_estimate;
  const comparables = report.comparables || [];
  const marketComparison = report.market_comparison;

  // Prepare chart data
  const rentDistribution = comparables.reduce((acc, comp) => {
    const bucket = Math.floor(comp.rent / 250) * 250;
    const key = `$${bucket}-${bucket + 250}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const chartData = Object.entries(rentDistribution)
    .map(([range, count]) => ({ range, count }))
    .sort((a, b) => parseInt(a.range.replace(/\D/g, "")) - parseInt(b.range.replace(/\D/g, "")));

  const getComparisonColor = () => {
    if (!marketComparison) return "slate";
    if (marketComparison.status === "above_market") return "emerald";
    if (marketComparison.status === "below_market") return "red";
    return "slate";
  };

  const comparisonColor = getComparisonColor();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Link to="/" className="flex items-center space-x-2">
                <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-14 w-auto" />
                
              </Link>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleShare}
                disabled={shareLoading}
                data-testid="share-btn"
              >
                {shareLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : copied ? (
                  <Check className="h-4 w-4 mr-2" />
                ) : (
                  <Share2 className="h-4 w-4 mr-2" />
                )}
                {copied ? "Copied!" : "Share"}
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportCSV}
                data-testid="export-csv-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExportPDF}
                disabled={pdfLoading}
                data-testid="export-pdf-btn"
              >
                {pdfLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {pdfLoading ? "Generating..." : "PDF"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Property Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-navy-900 mb-2">
                {report.address}
              </h1>
              <p className="text-slate-600 flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                {report.city}, {report.state} {report.zip_code}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="px-3 py-1">
                <Home className="h-3 w-3 mr-1" />
                {report.property_type.replace("_", " ")}
              </Badge>
              <Badge variant="secondary" className="px-3 py-1">
                <Bed className="h-3 w-3 mr-1" />
                {report.bedrooms} BR
              </Badge>
              <Badge variant="secondary" className="px-3 py-1">
                <Bath className="h-3 w-3 mr-1" />
                {report.bathrooms} BA
              </Badge>
              {report.sqft && (
                <Badge variant="secondary" className="px-3 py-1">
                  <Square className="h-3 w-3 mr-1" />
                  {report.sqft.toLocaleString()} sqft
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-navy-900 text-white" data-testid="estimated-rent-card">
            <CardContent className="p-6">
              <p className="text-sm text-slate-300 mb-1">Estimated Rent</p>
              <p className="font-data text-3xl font-bold">
                ${rentEstimate.estimated_rent.toLocaleString()}
                <span className="text-lg font-normal text-slate-400">/mo</span>
              </p>
            </CardContent>
          </Card>

          <Card data-testid="market-average-card">
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Market Average</p>
              <p className="font-data text-2xl font-bold text-navy-900">
                ${rentEstimate.rent_average.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="market-range-card">
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Market Range</p>
              <p className="font-data text-lg font-bold text-navy-900">
                ${rentEstimate.rent_low.toLocaleString()} - ${rentEstimate.rent_high.toLocaleString()}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="comps-card">
            <CardContent className="p-6">
              <p className="text-sm text-slate-500 mb-1">Comparables</p>
              <p className="font-data text-2xl font-bold text-navy-900">
                {rentEstimate.comps_count}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Market Comparison Alert */}
        {marketComparison && (
          <Card className={`mb-8 border-l-4 border-l-${comparisonColor}-500`} data-testid="market-comparison-card">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                {marketComparison.status === "above_market" ? (
                  <TrendingUp className="h-8 w-8 text-emerald-500 flex-shrink-0" />
                ) : marketComparison.status === "below_market" ? (
                  <TrendingDown className="h-8 w-8 text-red-500 flex-shrink-0" />
                ) : null}
                <div>
                  <p className="font-semibold text-navy-900 mb-1">
                    Your rent is {Math.abs(marketComparison.difference_percent).toFixed(1)}% {marketComparison.status.replace("_", " ")}
                  </p>
                  <p className="text-slate-600">
                    At ${marketComparison.current_rent.toLocaleString()}/mo, you're 
                    {marketComparison.difference > 0 ? " above " : " below "}
                    the estimated market rent of ${marketComparison.estimated_rent.toLocaleString()}/mo 
                    by ${Math.abs(marketComparison.difference).toLocaleString()}.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs Section */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="map" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <Map className="h-4 w-4 mr-2" />
              Map
            </TabsTrigger>
            <TabsTrigger value="comps" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <FileText className="h-4 w-4 mr-2" />
              Comparables
            </TabsTrigger>
            <TabsTrigger value="insights" className="data-[state=active]:bg-navy-900 data-[state=active]:text-white">
              <Sparkles className="h-4 w-4 mr-2" />
              AI Insights
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Rent Distribution Chart */}
              <Card data-testid="rent-distribution-chart">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Rent Distribution</CardTitle>
                  <CardDescription>Distribution of comparable rents in the area</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="range" fontSize={11} tick={{ fill: '#64748b' }} />
                        <YAxis fontSize={11} tick={{ fill: '#64748b' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Bar dataKey="count" fill="#2DD4BF" radius={[4, 4, 0, 0]}>
                          {chartData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.range.includes(String(Math.floor(rentEstimate.estimated_rent / 250) * 250)) ? "#0B1120" : "#2DD4BF"} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Statistics Summary */}
              <Card data-testid="statistics-summary">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Market Statistics</CardTitle>
                  <CardDescription>Key metrics for your analysis</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">25th Percentile</span>
                      <span className="font-data font-semibold text-navy-900">
                        ${rentEstimate.percentile_25.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">Median Rent</span>
                      <span className="font-data font-semibold text-navy-900">
                        ${rentEstimate.rent_median.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">75th Percentile</span>
                      <span className="font-data font-semibold text-navy-900">
                        ${rentEstimate.percentile_75.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-slate-100">
                      <span className="text-slate-600">Market Average</span>
                      <span className="font-data font-semibold text-navy-900">
                        ${rentEstimate.rent_average.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-slate-600">Properties Analyzed</span>
                      <span className="font-data font-semibold text-navy-900">
                        {rentEstimate.comps_count}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <Card data-testid="map-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Property Map</CardTitle>
                <CardDescription>Subject property and comparable locations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[500px] rounded-lg overflow-hidden">
                  <MapContainer
                    center={[report.latitude, report.longitude]}
                    zoom={14}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Subject Property Marker */}
                    <Marker position={[report.latitude, report.longitude]}>
                      <Popup>
                        <strong>Subject Property</strong><br />
                        {report.address}<br />
                        Est. Rent: ${rentEstimate.estimated_rent.toLocaleString()}/mo
                      </Popup>
                    </Marker>
                    {/* Search Radius Circle */}
                    <Circle
                      center={[report.latitude, report.longitude]}
                      radius={1609} // 1 mile in meters
                      pathOptions={{ color: '#2DD4BF', fillColor: '#2DD4BF', fillOpacity: 0.1 }}
                    />
                    {/* Comparable Properties */}
                    {comparables.slice(0, 10).map((comp, index) => (
                      <Marker key={index} position={[comp.latitude, comp.longitude]}>
                        <Popup>
                          <strong>{comp.address}</strong><br />
                          Rent: ${comp.rent.toLocaleString()}/mo<br />
                          {comp.bedrooms} BR / {comp.bathrooms} BA<br />
                          {comp.distance_miles.toFixed(2)} miles away
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Comparables Tab */}
          <TabsContent value="comps">
            <Card data-testid="comps-table-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comparable Properties</CardTitle>
                <CardDescription>{comparables.length} properties analyzed</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Address</TableHead>
                        <TableHead>Rent</TableHead>
                        <TableHead>Beds</TableHead>
                        <TableHead>Baths</TableHead>
                        <TableHead>Sq Ft</TableHead>
                        <TableHead>Distance</TableHead>
                        <TableHead>Similarity</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparables.map((comp, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{comp.address}</TableCell>
                          <TableCell className="font-data">${comp.rent.toLocaleString()}</TableCell>
                          <TableCell>{comp.bedrooms}</TableCell>
                          <TableCell>{comp.bathrooms}</TableCell>
                          <TableCell>{comp.sqft?.toLocaleString() || "-"}</TableCell>
                          <TableCell>{comp.distance_miles.toFixed(2)} mi</TableCell>
                          <TableCell>
                            <Badge variant={comp.similarity_score > 0.85 ? "default" : "secondary"}>
                              {(comp.similarity_score * 100).toFixed(0)}%
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="insights">
            <Card data-testid="ai-insights-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-teal-500" />
                  AI-Powered Insights
                </CardTitle>
                <CardDescription>Analysis generated by GPT-5.2</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose prose-slate max-w-none">
                  {report.ai_insights ? (
                    <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                      {report.ai_insights}
                    </div>
                  ) : (
                    <p className="text-slate-500 italic">
                      AI insights not available for this report. Upgrade to Pro to unlock AI-powered analysis.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Report Footer */}
        <div className="mt-8 pt-8 border-t border-slate-200">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Report generated: {new Date(report.created_at).toLocaleString()}
            </div>
            <div>Report ID: {report.report_id}</div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReportPage;
